// Pure, framework-free classification for the Franchise Inventory surface (design
// docs/projects/franchise-lifecycle-surfaces-design.md §3, §5). GET /franchise-inventory returns the
// shared, STATUS-FREE lifecycle read (one factual row per warehouse-known franchise); this helper derives
// each row's lifecycle STATUS, the sort, and the summary counts — Inventory's counterpart to
// onboardingRollup. It is a SUPERSET of the onboarding classification: the same three flag=1 states plus
// "Former (data retained)" for flag-off franchises whose warehouse history is kept. Kept here (not in SQL)
// so it is unit-testable without a DB, mirroring onboardingRollup / eligibilityRollup.
//
// The facts arrive from SQL as integer 0/1 (JSON numbers); treat them tolerantly (1 | true | "1") so a
// driver/serialization change can't silently flip a status (the BIT-vs-boolean trap the sibling helpers
// also guard against).

const isTrue = (v) => v === 1 || v === true || v === "1";

// Lifecycle status vocabulary, most-actionable first. `order` drives the sort; `label` is the human string;
// the component maps the key → chip colour / subdued styling. KEYS are stable identifiers; only LABELS are
// worded for the UI. Formers sort LAST — they are historical reference, not pending work.
//   - not_onboarded  => "Not onboarded":         flagged LK/SI, no warehouse data (a FACT, not a judgement).
//   - no_eligibility => "No eligibility period":  flagged, data present, no LK/SI eligibility period yet.
//   - onboarded      => "Onboarded":              flagged, data present, eligibility period exists.
//   - former         => "Former (data retained)": flag OFF but warehouse history retained (this population
//                       only ever includes a flag-off row when it has data, so "data retained" always holds).
export const STATUS_META = {
  not_onboarded: { label: "Not onboarded", order: 0 },
  no_eligibility: { label: "No eligibility period", order: 1 },
  onboarded: { label: "Onboarded", order: 2 },
  former: { label: "Former (data retained)", order: 3 },
};

// A franchise's lifecycle status from its factual axes (is_lksi flag + warehouse data + eligibility):
//   flag off                                  => former          (data-retained historical; flag is the tell)
//   flag on, no warehouse data                => not_onboarded   (THE onboarding ping)
//   flag on, data, no eligibility row         => no_eligibility  (onboarded, needs Audit config)
//   flag on, data, has eligibility row        => onboarded       (fully set up)
// The flag is checked FIRST because it is the axis that distinguishes Inventory from Onboarding Candidates
// (design §3): a flag-off row is a former regardless of its data/eligibility.
export function classifyFranchise(franchise = {}) {
  if (!isTrue(franchise.is_lksi)) return "former";
  if (!isTrue(franchise.warehouse_data_found)) return "not_onboarded";
  if (!isTrue(franchise.has_eligibility)) return "no_eligibility";
  return "onboarded";
}

// Attach `status` to every franchise and sort action-first (former last), then by com_code so the order is
// stable and historical rows sink to the bottom. Does not mutate the input.
export function rollupFranchises(franchises = []) {
  const rows = franchises.map((f) => ({ ...f, status: classifyFranchise(f) }));
  rows.sort((a, b) => {
    const oa = STATUS_META[a.status].order;
    const ob = STATUS_META[b.status].order;
    if (oa !== ob) return oa - ob;
    return String(a.com_code).localeCompare(String(b.com_code));
  });
  return rows;
}

// Summary chips: total + one count per lifecycle status.
export function summarizeFranchises(rows = []) {
  return {
    total: rows.length,
    notOnboarded: rows.filter((r) => r.status === "not_onboarded").length,
    noEligibility: rows.filter((r) => r.status === "no_eligibility").length,
    onboarded: rows.filter((r) => r.status === "onboarded").length,
    former: rows.filter((r) => r.status === "former").length,
  };
}
